<template>
  <b-card title="Resultado de Busqueda" v-if="product" >

    <!-- search input -->
    <div class="custom-search d-flex justify-content-end">
      <b-form-group>
        <div class="d-flex align-items-center">
          <label class="mr-1">Buscar</label>
          <b-form-input
            v-model="searchTerm"
            placeholder="Buscar"
            type="text"
            class="d-inline-block"
          />
        </div>
      </b-form-group>
    </div>

    <!-- table -->
    <vue-good-table
      :columns="columns"
      :rows="[product]"
      :rtl="direction"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :select-options="{
        enabled: false,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
        selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: Name -->
        <span
          v-if="props.column.field === 'serial'"
          class="text-nowrap"
        >
          <span class="text-nowrap">{{ props.row.serial }}</span><br>
        </span>

        <span
          v-else-if="props.column.field === 'productType'"
          class="text-nowrap"
        >
          <span class="text-nowrap">{{ props.row.product_type.name }}</span>
        </span>

        <!-- Column: Status -->
        <span v-else-if="props.column.field === 'numberOrder'"
          class="text-nowrap">
          <span class="text-nowrap">{{ props.row.number_order }}</span>
        </span>

        <span
          v-else-if="props.column.field === 'created_at'"
        >
          {{ formatDateVariant(props.row.created_at) }}
        </span>

        <span
          v-else-if="props.column.field === 'updated_at'"
        >
          {{ formatDateVariant(props.row.updated_at) }}
        </span>

        <span
          v-else-if="props.column.field === 'actions'"
        >
          <b-button
            v-if="[1].includes(userData.user_profile_id)"
            variant="secondary"
            size="sm"
            :to="{
              name: 'traceability', 
              params: { 
                loggable_type: 'producto',
                loggable_id: props.row.id,
              }
            }"
          >
            Trazabilidad
          </b-button>
          <b-button
            v-if="[1].includes(userData.user_profile_id)"
            class="ml-1"
            variant="danger"
            size="sm"
            @click="confirmDelete(props.row.serial)"
          >
            Eliminar
          </b-button>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap ">
              Mostrando 1 a
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['10','20','30','40','50']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> de {{ props.total }} items </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- modal -->
    <b-modal
      v-model="confirmDeleteModal"
      title="Eliminar serial"
      title-tag="div"
      no-close-on-backdrop
      no-close-on-esc
    >
      <p class="my-3">¿Estás seguro de que deseas eliminar el siguiente serial: <b class="text-danger">{{ productSerialDelete }}</b>? Esta acción no se puede deshacer.</p>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-between">
          <b-card-text class="mb-0">
            Presiona <em>'Eliminar'</em> para continuar
          </b-card-text>
          <b-button
            variant="danger"
            size="sm"
            @click="deleteSerial()"
          >
            Eliminar
          </b-button>
        </div>
      </template>
    </b-modal>

  </b-card>
  
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import {
  BAvatar, BBadge, BPagination, BFormGroup, BFormInput, BFormSelect, BDropdown, BDropdownItem, BCard, BButton, BModal, BCardText,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import axios from 'axios'
import {constants} from "@/helpers/constants"
import {formatDate} from "@/helpers/utils"
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCardCode,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BCard,
    BButton,
    BModal,
    BCardText,
  },
  data() {
    return {
      host: constants.hostUrl,
      accessToken: localStorage.getItem('accessToken'),
      userData: JSON.parse(localStorage.getItem('userData')),
      products: [],
      order: null,
      pageLength: 20,
      dir: false,
      columns: [
        {
          label: 'ID',
          field: 'id',
        },
        {
          label: 'Serial',
          field: 'serial',
        },
        {
          label: 'Tipo de producto',
          field: 'productType',
        },
        {
          label: 'Numero de transporte',
          field: 'numberOrder',
        },
        {
          label: 'Novedad',
          field: 'newness.newness_type.name',
        },
        {
          label: 'Creado',
          field: 'created_at',
        },
        {
          label: 'Actualizado',
          field: 'updated_at',
        },
        {
          label: 'Acciones',
          field: 'actions',
        },
      ],
      searchTerm: '',
      status: [{
        1: 'En alistamiento',
        2: 'Despachado',
        3: 'Cancelado',
        4: 'Finalizado',
        5: 'Recibido',
      },
      {
        1: 'light-primary',
        2: 'light-success',
        3: 'light-danger',
        4: 'light-warning',
        5: 'light-info',
      }],
      confirmDeleteModal: false,
      productSerialDelete: null,
    }
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  computed: {
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        'En alistamiento'      : 'light-primary',
        Despachado : 'light-success',
        Cancelado     : 'light-danger',
        Finalizado     : 'light-warning',
        Recibido      : 'light-info',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    formatDateVariant() {
      return date => formatDate(date)
    },
  },
  created() {

  },
  methods: {
    confirmDelete(productSerial) {
      this.confirmDeleteModal = true;
      this.productSerialDelete = productSerial;
    },
    deleteSerial() {
      this.confirmDeleteModal = false;
      console.log(this.productSerialDelete);
      this.$bvModal
        .msgBoxConfirm('Por favor confirme si quiere eliminar el serial definitivamente.', {
          title: 'Eliminar serial',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          console.log('eliminar')

          let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + this.accessToken
          }
          axios.delete(this.host + '/api/front-web/products/' + this.productSerialDelete, { headers }).then(response => {
            console.log('remove-serial: ', response)

            if(response.data.status) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: response.data.message,
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })

              setTimeout(() => {
                window.location.reload();
              }, 1500);

            } else {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: response.data.message,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              })
            }

          }).catch( err => {
            console.log(err)
          })

        })
    }
  },
}
</script>
